import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import './UserMenu.css';

export const UserMenu: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const getInitials = (user: any): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user.username
      .split(' ')
      .map((word: string) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getDisplayName = (user: any): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="user-menu" ref={menuRef}>
      <button 
        className="user-menu-trigger"
        onClick={toggleMenu}
        aria-label={t('userMenu')}
      >
        <div className="user-avatar">
          <span className="user-initials">
            {getInitials(user)}
          </span>
        </div>
        <span className="user-name">
          {getDisplayName(user)}
        </span>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-info">
              <div className="user-avatar-large">
                <span className="user-initials-large">
                  {getInitials(user)}
                </span>
              </div>
              <div className="user-details">
                <span className="user-name-large">{getDisplayName(user)}</span>
                <span className="user-email">{user.email}</span>
                {user.role && (
                  <span className="user-role">{
                    t(user.role === 'project_manager'
                      ? 'roleProjectManager'
                      : user.role === 'admin'
                        ? 'roleAdmin'
                        : user.role === 'worker'
                          ? 'roleWorker'
                          : 'roleViewer')
                  }</span>
                )}
              </div>
            </div>
          </div>

          <div className="user-menu-divider" />

          <div className="user-menu-items">
            <button
              className="user-menu-item logout-item"
              onClick={handleLogout}
            >
              <LogoutIcon className="menu-icon" />
              <span>{t('logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};